/**
 * Utility functions for handling errors
 */

/**
 * Get a user-friendly error message from a Firebase error
 * @param error Firebase error object
 * @returns User-friendly error message
 */
export const getFirebaseErrorMessage = (error: any): string => {
  // If the error is already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // If the error has a message property, use that
  if (error.message) {
    return error.message;
  }

  // Handle specific Firebase error codes
  if (error.code) {
    switch (error.code) {
      case 'auth/user-not-found':
        return 'No user found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'The email address is not valid.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please try a different one.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use a stronger password.';
      case 'auth/operation-not-allowed':
        return 'This operation is not allowed.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with the same email but different sign-in credentials.';
      case 'auth/invalid-credential':
        return 'The credential is invalid or has expired.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed before completing the sign in.';
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for OAuth operations.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful login attempts. Please try again later.';
      default:
        return `Authentication error: ${error.code}`;
    }
  }

  // Default error message
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get a user-friendly error message from an API error
 * @param error API error object
 * @returns User-friendly error message
 */
/**
 * The machine-readable code the API attaches to an error, when it has one.
 *
 * Used to branch on specific failures (for example USER_HAS_CONTENT, which means
 * the member has to have their content transferred before they can be removed)
 * instead of matching on message text.
 */
export const getApiErrorCode = (error: any): string | undefined => {
  return error?.response?.data?.error?.code;
};

/**
 * Extra context the API attached to an error, when it has any.
 */
export const getApiErrorDetails = <T = Record<string, unknown>>(error: any): T | undefined => {
  return error?.response?.data?.error?.details;
};

export const getApiErrorMessage = (error: any): string => {
  // If the error is already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // Handle Axios error responses with backend error messages
  if (error.response && error.response.data) {
    // Check for our API format: { success: false, error: { message: 'Error message' } }
    if (error.response.data.error && error.response.data.error.message) {
      return error.response.data.error.message;
    }
    
    // Check for simple error message in response data
    if (error.response.data.message) {
      return error.response.data.message;
    }
    
    // If response data has an error property with a message
    if (error.response.data.error && typeof error.response.data.error === 'string') {
      return error.response.data.error;
    }
  }

  // If the error has a message property, use that
  if (error.message && error.message !== 'Request failed with status code 400' && 
      error.message !== 'Request failed with status code 500') {
    return error.message;
  }

  // If the error has an error property with a message, use that
  if (error.error && error.error.message) {
    return error.error.message;
  }

  // Default error message based on status code
  if (error.response) {
    switch (error.response.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized to perform this action.';
      case 403:
        return 'You do not have permission to access this resource.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This operation could not be completed due to a conflict.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'An internal server error occurred. Please try again later.';
      default:
        return `Server error (${error.response.status}). Please try again later.`;
    }
  }

  // Default error message
  return 'An unexpected error occurred. Please try again.';
};
